/**
 * Admins by name (decision 38). The first admin link is the owner's: it is the link every tool
 * and every phone already has, and only the owner's own screen can replace it. Any admin can give
 * someone else an admin link of their own, with their name on it: what that person does is logged
 * under that name whatever their phone says, and taking it away stops only their phone.
 *
 * Pure: the Durable Object, the mock server and the tests all use these.
 */

import type { Links } from "./protocol";

/** The longest name a person can go by, here and on the admin screen's "who are you". */
export const NAME_MAX = 20;
/** The most named admins a tournament can have, besides the owner. */
export const ADMINS_MAX = 12;

/** One person's own admin link. */
export interface NamedAdmin {
  id: string;
  name: string;
  token: string;
  addedAt: number;
  /** Who made it: "owner", or another named admin as the log says them ("admin:Piri"). */
  addedBy: string;
}

/** The role links the server keeps. `admin` is the owner's. */
export interface Tokens {
  projector: string;
  courts: string[];
  admin: string;
  players: string;
  /** Absent on tokens made before the watcher existed; filled in on load. */
  watcher?: string;
  /** The watch remote. Absent on tokens made before it existed; filled in on load. */
  remote?: string;
  /** The named admins. Absent on tokens made before they existed: nobody but the owner. */
  admins?: NamedAdmin[];
}

/**
 * Spaces run together and trimmed, at most NAME_MAX long. Characters that print as nothing (a
 * zero-width space, a direction mark) are taken out first, so a name cannot look like another
 * admin's and still count as different. Anything that is not text is no name at all.
 */
export function cleanName(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw.replace(/\p{Cf}/gu, "").replace(/\s+/g, " ").trim().slice(0, NAME_MAX);
}

/** Who an admin token belongs to: the owner, one named admin, or nobody. */
export function adminFor(t: Tokens | null, token: string): { owner: true } | { owner: false; admin: NamedAdmin } | null {
  if (!t || !token) return null;
  if (token === t.admin) return { owner: true };
  const admin = (t.admins ?? []).find((a) => a.token === token);
  return admin ? { owner: false, admin } : null;
}

/**
 * Whether an admin socket may still act. The owner's goes by the owner token; a named admin's only
 * while that admin is still on the list with the same token. A socket opened before named admins
 * existed carries no admin id, and was opened with the owner link.
 */
export function adminStillValid(t: Tokens | null, who: { token: string; adminId?: string }): boolean {
  if (!t) return false;
  if (who.adminId) return (t.admins ?? []).some((a) => a.id === who.adminId && a.token === who.token);
  return who.token === t.admin;
}

/** Add someone. The reason in words when they cannot be added. */
export function addAdmin(
  list: readonly NamedAdmin[],
  raw: unknown,
  made: { id: string; token: string; at: number; by: string },
): { list: NamedAdmin[]; admin: NamedAdmin } | { reason: string } {
  const name = cleanName(raw);
  if (!name) return { reason: "Type the new admin's name first." };
  const same = list.find((a) => a.name.toLowerCase() === name.toLowerCase());
  if (same) return { reason: `${same.name} is already an admin.` };
  if (list.length >= ADMINS_MAX) return { reason: `That is ${ADMINS_MAX} admins already. Take one away first.` };
  const admin: NamedAdmin = { id: made.id, name, token: made.token, addedAt: made.at, addedBy: made.by };
  return { list: [...list, admin], admin };
}

/** Take someone away. Null when they are not on the list. */
export function removeAdmin(list: readonly NamedAdmin[], id: string): { list: NamedAdmin[]; admin: NamedAdmin } | null {
  const admin = list.find((a) => a.id === id);
  return admin ? { list: list.filter((a) => a.id !== id), admin } : null;
}

/** A new link for one person; their old one stops. Null when they are not on the list. */
export function renewAdmin(list: readonly NamedAdmin[], id: string, token: string): { list: NamedAdmin[]; admin: NamedAdmin } | null {
  const was = list.find((a) => a.id === id);
  if (!was) return null;
  const admin = { ...was, token };
  return { list: list.map((a) => (a.id === id ? admin : a)), admin };
}

/** Every link, for the screen that will see them. */
export function allLinks(t: Tokens | null, origin: string, tournament: string): Links {
  if (!t) return { projector: "", calibration: "", courts: [], admin: "", players: "", watcher: "", remote: "", admins: [] };
  const q = tournament === "main" ? "" : `?t=${tournament}`;
  return {
    projector: `${origin}/p/${t.projector}${q}`,
    calibration: `${origin}/p/${t.projector}/cal${q}`,
    courts: t.courts.map((c, i) => `${origin}/c/${c}/${i + 1}${q}`),
    admin: `${origin}/a/${t.admin}${q}`,
    players: `${origin}/j/${t.players}${q}`,
    watcher: t.watcher ? `${origin}/w/${t.watcher}${q}` : "",
    remote: t.remote ? `${origin}/api/remote/${t.remote}${q}` : "",
    admins: (t.admins ?? []).map((a) => ({ id: a.id, name: a.name, link: `${origin}/a/${a.token}${q}`, addedBy: a.addedBy })),
  };
}

/**
 * The links one screen is sent. An admin sees every link, the named admins' too, but the owner's
 * link only on the owner's own screen. The projector sees the players' link and nothing else.
 */
export function linksFor(all: Links, who: { role: string; owner?: boolean }): Links | null {
  if (who.role === "admin") return who.owner === false ? { ...all, admin: "" } : all;
  if (who.role === "projector") return { ...all, projector: "", calibration: "", courts: [], admin: "", watcher: "", remote: "", admins: [] };
  return null;
}

/**
 * Who made a named admin's link, in words for the screen that shows it: "you", "the owner", or
 * the admin by name. `me` is the screen's own: the owner, or a named admin by name.
 */
export function addedByWords(addedBy: string | undefined, me: { owner: boolean; name?: string | null }): string {
  if (!addedBy) return "";
  if (addedBy === "owner") return me.owner ? "Added by you" : "Added by the owner";
  const name = addedBy.startsWith("admin:") ? addedBy.slice("admin:".length) : addedBy;
  if (!me.owner && me.name && name.toLowerCase() === me.name.toLowerCase()) return "Added by you";
  return name ? `Added by ${name}` : "";
}
